<template>
  <div class="purchases-table-wrapper">
    <LoadingTemplate v-if="isRequesting" />

    <template v-else>
      <div class="table-head" ref="head" @wheel.prevent.stop>
        <div class="table-head-content">
          <PurchasesTreeTableHeader
            :columns="visibleColumns"
            :widths="localColumnWidths"
            @resize="startResize"
            :is-resizing="!!resizing"
          />

          <PurchasesTreeTableFilters
            :columns="visibleColumns"
            :widths="localColumnWidths"
            :is-resizing="!!resizing"
          />
        </div>
      </div>

      <div
        class="table-body"
        ref="scroll"
        @scroll="onScroll"
        @mouseover="onTableMouseOver"
        @mouseout="onTableMouseOut"
      >
        <div
          :style="{
            width: contentWidth + 'px',
            height: topSpacerHeight + 'px',
          }"
        />
        <div class="table-content">
          <PurchasesTreeTableRow
            v-for="row in virtualRows"
            :key="row.id"
            :purchase="row"
            :columns="preparedColumns"
            :highlight-new="isHighlightNewRows"
            :line-heights-type="lineHeightsType"
            :animation="rowAnimations[row.id]"
          />
        </div>
        <div
          :style="{
            width: contentWidth + 'px',
            height: bottomSpacerHeight + 'px',
          }"
        />
        <TreeTableNotesPopover />
      </div>
    </template>
    <TreeTableExceptionWordPopover />
    <TreeTableTooltip
      :visible="tooltipController.state.visible"
      :content="tooltipController.state.content"
      :tooltip-style="tooltipController.state.style"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import LoadingTemplate from "./LoadingTemplate.vue";
import PurchasesTreeTableHeader from "./PurchasesTreeTableHeader.vue";
import PurchasesTreeTableRow from "./PurchasesTreeTableRow.vue";
import TreeTableTooltip from "./TreeTableTooltip.vue";
import { createTooltipController } from "../../utils/useTooltip";
import PurchasesTreeTableFilters from "./purchasesTreeTableFilters/PurchasesTreeTableFilters.vue";
import CELL_COMPONENTS from "../../utils/tableCellRegistry.js";
import DefaultTooltip from "./tooltips/DefaultTooltip.vue";
import { eventBus } from "@/main";
import { isEmpty, isNil } from "@/utils/lodash";
import TreeTableNotesPopover from "./TreeTableNotesPopover.vue";
import { createNotesController } from "../../utils/useNotes";
import { createExceptionWordsController } from "../../utils/useExceptionWord.js";
import TreeTableExceptionWordPopover from "./TreeTableExceptionWordPopover.vue";

export default {
  name: "PurchasesTreeTable",
  components: {
    PurchasesTreeTableHeader,
    PurchasesTreeTableRow,
    LoadingTemplate,
    TreeTableTooltip,
    PurchasesTreeTableFilters,
    TreeTableNotesPopover,
    TreeTableExceptionWordPopover,
  },
  props: {
    purchases: {
      type: Array,
      required: true,
    },
    isRequesting: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // resize
      resizing: null,
      localColumnWidths: {},
      minColumnWidth: 50,

      // selection / tree
      selectedMap: {},
      expandedMap: {},
      expandAll: null,

      // highlight
      isHighlightNewRows: true,
      highlightTimer: null,

      // tooltip
      tooltipController: createTooltipController(),

      //notes
      notesController: createNotesController(),

      //exception words
      exceptionWordsController: createExceptionWordsController(),

      // virtualization
      scrollTop: 0,
      scrollLeft: 0,
      scrollDirection: 1,
      viewportHeight: 0,
      overscanBefore: 1,
      overscanAfter: 12,
      overscanNear: 1,
      overscanFar: 12,

      // scrolling
      previousTabId: null,
      savedScrollTop: 0,
      savedScrollLeft: 0,
      isScrolling: false,
      scrollTimer: null,
      isProgrammaticScroll: false,

      // animations
      isAnimatingRows: false,
      animationCleanupTimer: null,
      rowAnimations: {},
      lastCollapsingRowId: null,
    };
  },

  computed: {
    ...mapGetters("purchasesTableSettings", [
      "lineHeightsType",
      "purchasesTableColumns",
    ]),
    ...mapGetters("profile", ["profileId"]),
    ...mapGetters("filterTabs", ["currentTab"]),
    ...mapGetters("columnsFilters", ["columnFiltersByKey"]),
    ...mapGetters("columnsSort", ["columnSortByKey"]),

    contentWidth() {
      return this.visibleColumns.reduce((sum, column) => {
        return (
          sum +
          (CELL_COMPONENTS[column.field]?.width ??
            this.localColumnWidths[column.field] ??
            column.width)
        );
      }, 0);
    },

    visibleColumns() {
      const columns = Object.values(this.purchasesTableColumns)
        .filter((col) => !col.hide)
        .sort((a, b) => a.index - b.index);

      const favoritesColumn = columns.find(
        (column) => column.field === "favorites",
      );

      const hiddenColumn = columns.find((column) => column.field === "hidden");

      if (!favoritesColumn || !hiddenColumn) {
        return columns;
      }

      return [
        hiddenColumn,
        favoritesColumn,
        ...columns.filter(
          (column) => column.field !== "favorites" && column.field !== "hidden",
        ),
      ];
    },
    computedColumnStyles() {
      const styles = {};

      for (const column of this.visibleColumns) {
        const width =
          CELL_COMPONENTS[column.field]?.width ??
          this.localColumnWidths[column.field] ??
          column.width;

        styles[column.field] = {
          flex: `0 0 ${width}px`,
          width: `${width}px`,
          height: `${this.lineHeightsType.height}px`,
        };
      }

      return styles;
    },
    preparedColumns() {
      return this.visibleColumns.map((column) => {
        const cell = CELL_COMPONENTS[column.field] || CELL_COMPONENTS.default;

        return {
          ...column,
          cell,
          style: this.computedColumnStyles[column.field],
          tooltipEnabled: cell?.hasTooltip !== false,
          tooltipComponent: cell?.tooltipComponent || DefaultTooltip,
        };
      });
    },

    rowHeight() {
      return this.lineHeightsType.height;
    },
    startIndex() {
      return Math.max(
        0,
        Math.floor(this.scrollTop / this.rowHeight) - this.overscanBefore,
      );
    },
    endIndex() {
      return Math.min(
        this.visibleRows.length,
        Math.floor(this.scrollTop / this.rowHeight) +
          Math.ceil(this.viewportHeight / this.rowHeight) +
          this.overscanAfter,
      );
    },
    virtualRows() {
      return this.visibleRows.slice(this.startIndex, this.endIndex);
    },
    topSpacerHeight() {
      return this.startIndex * this.rowHeight;
    },
    bottomSpacerHeight() {
      return (this.visibleRows.length - this.endIndex) * this.rowHeight;
    },

    tree() {
      const map = {};

      this.purchases.forEach((item) => {
        map[item.id] = { ...item, children: [] };
      });

      this.purchases.forEach((item) => {
        if (item.parentId) {
          map[item.parentId]?.children.push(map[item.id]);
        }
      });

      return map;
    },
    visibleRows() {
      const result = [];

      this.purchases.forEach((item) => {
        if (!item.parentId) {
          const children = this.tree[item.id]?.children || [];

          result.push(this.prepareRow(item, false, children.length === 0));

          if (children.length && this.expandedMap[item.id]) {
            children.forEach((child, index) => {
              result.push(
                this.prepareRow(
                  child,
                  index === 0,
                  index === children.length - 1,
                ),
              );
            });
          }
        }
      });

      return result;
    },
    isCollapsed() {
      return this.purchases
        .filter((item) => !item.parentId && this.hasChildren(item.id))
        .some((item) => !this.expandedMap[item.id]);
    },

    selectedRowsArray() {
      return Object.keys(this.selectedMap).filter((id) => {
        return this.selectedMap[id];
      });
    },
    isAllSelected() {
      const allIds = this.purchases.map((i) => i.id);
      return (
        allIds.length > 0 && allIds.every((id) => this.selectedMap[id] === true)
      );
    },
    isIndeterminate() {
      const values = Object.values(this.selectedMap);
      const hasSelected = values.some((v) => v === true);
      const hasNotAllSelected = !this.isAllSelected;
      const hasIndeterminate = values.some((v) => v === "indeterminate");

      return (hasSelected && hasNotAllSelected) || hasIndeterminate;
    },
  },
  methods: {
    ...mapActions("purchasesTableSettings", ["updatePurchaseTableColumn"]),
    ...mapActions("enums", ["getSectorsTypes"]),
    ...mapActions("purchasesTableSettings", [
      "updatePurchaseTableColumn",
      "updatePurchaseTableColumns",
    ]),
    ...mapActions("columnsFilters", ["removeColumnFilters"]),
    ...mapActions("columnsSort", ["removeColumnSort"]),
    ...mapActions("notes", ["fetchProfileNotes"]),

    pushRoute() {
      if (isNil(this.$route.params.id)) {
        return;
      }

      if (
        parseInt(this.$route.params.id) === this.currentTab.id &&
        isEmpty(this.$route.query)
      ) {
        return;
      }

      this.$router.push(`/purchases/${this.currentTab.id}`);
    },
    async toggleColumnVisibility(columnName) {
      const column = this.purchasesTableColumns[columnName];

      if (!column) return;

      const isHidden = column.hide;

      await this.updatePurchaseTableColumn({
        columnName,
        columnProperty: "hide",
        value: !isHidden,
      });

      const filters = this.columnFiltersByKey(columnName);

      if (filters && !isEmpty(filters)) {
        this.removeColumnFilters(columnName);
      }

      const sort = this.columnSortByKey(columnName);

      if (sort && !isEmpty(sort)) {
        this.removeColumnSort(columnName);
      }

      this.pushRoute();
    },
    moveColumnByIndex(fromIndex, toIndex) {
      const entries = Object.entries(this.purchasesTableColumns).sort(
        (a, b) => a[1].index - b[1].index,
      );

      const moved = entries.splice(fromIndex, 1)[0];

      entries.splice(toIndex, 0, moved);

      const updatedColumns = Object.fromEntries(
        entries.map(([key, column], index) => [
          key,

          {
            ...column,

            index,
          },
        ]),
      );

      this.updatePurchaseTableColumns(updatedColumns);
    },

    isOverflowing(el) {
      if (!el) return false;

      const lineClamp = getComputedStyle(el).webkitLineClamp;

      if (lineClamp) {
        return (
          el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth
        );
      }

      return false;
    },
    onTableMouseOver(e) {
      if (this.isScrolling) {
        this.tooltipController.leaveTarget();
        return;
      }
      const cell = e.target.closest(".cell");

      if (!cell) return;

      const field = cell.dataset.field;
      const column = this.preparedColumns.find(
        (column) => column.field === field,
      );

      if (!column?.tooltipEnabled) return;

      const contentEl =
        cell.querySelector(".proposal-name-text") ||
        cell.querySelector(".cell-content");

      if (!this.isOverflowing(contentEl)) return;

      const rowEl = cell.closest(".content-row");

      if (!rowEl) return;

      const row = this.virtualRows.find(
        (item) => String(item.id) === rowEl.dataset.id,
      );

      if (!row) return;

      this.tooltipController.enterTarget();

      this.tooltipController.show({
        target: cell,
        component: column.tooltipComponent,
        componentProps: {
          value: row.values[column.field],
          row,
          column,
        },
      });
    },
    onTableMouseOut() {
      this.tooltipController.leaveTarget();
    },

    prepareValues(item) {
      const values = {};

      this.visibleColumns.forEach((column) => {
        let value;

        if (column.field === "favorites") {
          value = item.marks;
        } else {
          value = item[column.field];
        }

        values[column.field] = value ?? "-";
      });

      return values;
    },
    prepareRow(item, isFirstChild, isLastRow) {
      return {
        ...item,
        isFirstChild,
        isLastRow,
        isCollapsed:
          !item.parentId &&
          this.hasChildren(item.id) &&
          !this.expandedMap[item.id],
        values: this.prepareValues(item),
      };
    },
    hasChildren(id) {
      return (this.tree[id]?.children?.length || 0) > 0;
    },

    onScroll(e) {
      if (this.isProgrammaticScroll) return;

      const scrollTop = e.target.scrollTop;
      const scrollLeft = e.target.scrollLeft;

      if (this.$refs.head.scrollLeft !== scrollLeft) {
        this.$refs.head.scrollLeft = scrollLeft;
      }

      const direction =
        scrollTop > this.scrollTop
          ? 1
          : scrollTop < this.scrollTop
          ? -1
          : this.scrollDirection;
      const maxScroll =
        this.visibleRows.length * this.rowHeight - this.viewportHeight;
      const isNearBottom = scrollTop >= maxScroll - this.rowHeight * 2;

      if (!isNearBottom && direction !== this.scrollDirection) {
        this.scrollDirection = direction;

        if (direction === 1) {
          this.overscanBefore = this.overscanNear;
          this.overscanAfter = this.overscanFar;
        } else {
          this.overscanBefore = this.overscanFar;
          this.overscanAfter = this.overscanNear;
        }
      } else if (isNearBottom) {
        this.overscanBefore = this.overscanFar;
        this.overscanAfter = this.overscanNear;
      }

      this.saveScrollPosition(scrollTop, scrollLeft);

      if (!this.isScrolling) {
        this.isScrolling = true;
      }

      clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(() => {
        requestAnimationFrame(() => {
          this.isScrolling = false;
        });
      }, 120);
    },
    updateViewport() {
      const scroll = this.$refs.scroll;
      if (!scroll) return;
      this.viewportHeight = scroll.clientHeight;
    },
    resetScrollPosition() {
      this.savedScrollTop = 0;
      this.savedScrollLeft = 0;
      this.scrollTop = 0;
      this.scrollLeft = 0;
    },

    startResize(e, column) {
      const startX = e.clientX;
      const startWidth = this.localColumnWidths[column.field];

      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
      let raf = null;

      const onMouseMove = (event) => {
        const delta = event.clientX - startX;
        const newWidth = Math.max(this.minColumnWidth, startWidth + delta);

        if (raf) return;

        raf = requestAnimationFrame(() => {
          this.$set(this.localColumnWidths, column.field, newWidth);
          raf = null;
        });
      };

      const onMouseUp = () => {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);

        this.resizing = null;
        this.updatePurchaseTableColumn({
          columnName: column.field,
          columnProperty: "width",
          value: this.localColumnWidths[column.field],
        });
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);

      this.resizing = column;
    },

    maintainScrollPosition(action, targetRowId = null) {
      const scrollEl = this.$refs.scroll;
      if (!scrollEl) {
        action();
        return;
      }

      const currentScrollTop = scrollEl.scrollTop;

      let anchorId = targetRowId;
      let anchorIndex = -1;

      if (anchorId) {
        anchorIndex = this.visibleRows.findIndex(
          (r) => String(r.id) === String(anchorId),
        );
      }

      if (anchorIndex === -1) {
        const approximateIndex = Math.floor(currentScrollTop / this.rowHeight);
        anchorIndex = Math.max(
          0,
          Math.min(approximateIndex, this.visibleRows.length - 1),
        );
        if (this.visibleRows[anchorIndex]) {
          anchorId = this.visibleRows[anchorIndex].id;
        }
      }

      const anchorAbsoluteTop =
        anchorIndex !== -1 ? anchorIndex * this.rowHeight : 0;
      const itemOffsetFromViewport = anchorAbsoluteTop - currentScrollTop;

      this.isProgrammaticScroll = true;

      action();

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if (!scrollEl) {
            this.isProgrammaticScroll = false;
            return;
          }

          let newScrollTop = currentScrollTop;

          if (anchorId) {
            const newIndex = this.visibleRows.findIndex(
              (r) => String(r.id) === String(anchorId),
            );
            if (newIndex !== -1) {
              const newAnchorAbsoluteTop = newIndex * this.rowHeight;
              newScrollTop = newAnchorAbsoluteTop - itemOffsetFromViewport;
            }
          }

          const maxScroll = Math.max(
            0,
            this.visibleRows.length * this.rowHeight - this.viewportHeight,
          );
          const clampedScrollTop = Math.max(
            0,
            Math.min(newScrollTop, maxScroll),
          );

          scrollEl.scrollTop = clampedScrollTop;
          this.scrollTop = clampedScrollTop;
          this.savedScrollTop = clampedScrollTop;

          requestAnimationFrame(() => {
            this.isProgrammaticScroll = false;
          });
        });
      });
    },

    scheduleAnimationCleanup(lastRowId) {
      if (this.animationCleanupTimer) {
        clearTimeout(this.animationCleanupTimer);
      }
      this.animationCleanupTimer = setTimeout(() => {
        this.isAnimatingRows = false;

        const hasCollapsing = Object.values(this.expandedMap).includes(
          "collapsing",
        );

        if (hasCollapsing) {
          this.maintainScrollPosition(() => {
            Object.keys(this.expandedMap).forEach((id) => {
              if (this.expandedMap[id] === "collapsing") {
                this.$set(this.expandedMap, id, false);
              }
            });
            this.rowAnimations = {};
          }, lastRowId);
        } else {
          Object.keys(this.expandedMap).forEach((id) => {
            if (this.expandedMap[id] === "collapsing") {
              this.$set(this.expandedMap, id, false);
            }
          });
          this.rowAnimations = {};
        }

        this.lastCollapsingRowId = null;
      }, 300);
    },

    setExpanded(id, expand) {
      if (!this.hasChildren(id)) return;

      this.isAnimatingRows = true;
      this.lastCollapsingRowId = id;

      const children = this.tree[id]?.children || [];

      if (expand) {
        this.maintainScrollPosition(() => {
          this.$set(this.expandedMap, id, true);
          children.forEach((child) => {
            this.$set(this.rowAnimations, child.id, "enter");
          });
        }, id);
        this.scheduleAnimationCleanup(id);
      } else {
        this.$set(this.expandedMap, id, "collapsing");
        children.forEach((child) => {
          this.$set(this.rowAnimations, child.id, "leave");
        });
        this.scheduleAnimationCleanup(id);
      }
    },

    toggleExpand(id) {
      const expand = !this.expandedMap[id];
      this.setExpanded(id, expand);

      const stored = this.loadSavedExpandedMap();
      Object.assign(stored, { [id]: expand });
      localStorage.setItem(
        this.getExpandedMapStorageKey(),
        JSON.stringify(stored),
      );
    },

    toggleAll() {
      this.isAnimatingRows = true;
      const shouldExpand = this.isCollapsed;
      this.expandAll = shouldExpand;

      localStorage.setItem(
        this.getExpandAllStorageKey(),
        JSON.stringify(shouldExpand),
      );

      if (shouldExpand) {
        this.maintainScrollPosition(() => {
          const map = {};
          this.purchases.forEach((item) => {
            if (!item.parentId && this.hasChildren(item.id)) {
              map[item.id] = true;
              const children = this.tree[item.id]?.children || [];
              children.forEach((child) => {
                this.$set(this.rowAnimations, child.id, "enter");
              });
            }
          });
          this.expandedMap = map;

          localStorage.setItem(
            this.getExpandedMapStorageKey(),
            JSON.stringify({}),
          );
          this.$emit("collapse-state-change", this.isCollapsed);
        });
        this.scheduleAnimationCleanup(null);
      } else {
        const map = {};
        this.purchases.forEach((item) => {
          if (!item.parentId && this.hasChildren(item.id)) {
            map[item.id] = "collapsing";
            const children = this.tree[item.id]?.children || [];
            children.forEach((child) => {
              this.$set(this.rowAnimations, child.id, "leave");
            });
          }
        });
        this.expandedMap = map;

        localStorage.setItem(
          this.getExpandedMapStorageKey(),
          JSON.stringify({}),
        );

        this.scheduleAnimationCleanup(null);
      }
    },

    toggleSelect(row) {
      const next = !this.selectedMap[row.id];
      this.applySelection(row, next);

      if (row.parentId) {
        this.updateParentState(row.parentId);
      }
    },
    toggleSelectAll() {
      const roots = this.purchases.filter((i) => !i.parentId);

      const shouldSelect = !this.isAllSelected;

      roots.forEach((root) => {
        this.applySelection(root, shouldSelect);
      });
    },
    applySelection(row, value) {
      this.$set(this.selectedMap, row.id, value);

      const children = this.tree[row.id]?.children || [];

      children.forEach((child) => {
        this.applySelection(child, value);
      });

      if (row.parentId) {
        this.updateParentState(row.parentId);
      }
    },
    updateParentState(parentId) {
      const children = this.tree[parentId]?.children || [];

      const all = children.every((c) => this.selectedMap[c.id] === true);
      const none = children.every((c) => !this.selectedMap[c.id]);

      if (all) {
        this.$set(this.selectedMap, parentId, true);
      } else if (none) {
        this.$set(this.selectedMap, parentId, false);
      } else {
        this.$set(this.selectedMap, parentId, "indeterminate");
      }
    },

    getExpandedMapStorageKey() {
      return `expandedMap:${this.profileId}`;
    },
    loadSavedExpandedMap() {
      return (
        JSON.parse(localStorage.getItem(this.getExpandedMapStorageKey())) || {}
      );
    },
    getExpandAllStorageKey() {
      return `expandAll:${this.profileId}`;
    },
    loadSavedExpandAll() {
      const value = localStorage.getItem(this.getExpandAllStorageKey());

      if (!value) return false;

      return JSON.parse(value);
    },

    saveScrollPosition(scrollTop, scrollLeft) {
      this.previousTabId = this.$route.params.id;
      this.savedScrollTop = scrollTop;
      this.scrollTop = scrollTop;
      this.savedScrollLeft = scrollLeft;
      this.scrollLeft = scrollLeft;
    },
    restoreScrollPosition() {
      this.$nextTick(() => {
        const scroll = this.$refs.scroll;
        const tabId = this.$route.params.id;
        if (!scroll || tabId !== this.previousTabId) return;
        scroll.scrollTop = this.savedScrollTop;
        scroll.scrollLeft = this.savedScrollLeft;
      });
    },
  },
  watch: {
    purchases: {
      immediate: true,
      handler(list) {
        this.restoreScrollPosition();
        if (!list || !list.length) return;

        const savedExpandAll = this.loadSavedExpandAll();
        const savedExpandedMap = this.loadSavedExpandedMap();
        const map = {};

        this.expandAll = savedExpandAll;

        this.isHighlightNewRows = true;
        if (this.highlightTimer) {
          clearTimeout(this.highlightTimer);
        }
        this.highlightTimer = setTimeout(() => {
          this.isHighlightNewRows = false;
        }, 10000);

        list.forEach((item) => {
          if (!item.parentId && this.hasChildren(item.id)) {
            map[item.id] = savedExpandedMap[item.id] ?? savedExpandAll;
          }
        });

        this.expandedMap = map;
      },
    },

    visibleColumns: {
      immediate: true,
      handler(columns) {
        const map = {};

        columns.forEach((col) => {
          map[col.field] = col.width;
        });

        this.localColumnWidths = map;
      },
    },

    isRequesting(value) {
      this.selectedMap = {};
      if (!value) {
        this.$nextTick(() => {
          this.updateViewport();
        });
      }
    },
    "$route.query.page"(newPage, oldPage) {
      if (newPage !== oldPage) {
        this.resetScrollPosition();
      }
    },

    "$route.params.id"(newId, oldId) {
      if (newId !== oldId) {
        this.resetScrollPosition();
        this.previousTabId = newId;
      }
    },

    selectedMap: {
      immediate: true,
      deep: true,
      handler(selectedRows) {
        const rowsArray = Object.keys(selectedRows).filter((id) => {
          const row = this.tree[id];

          return selectedRows[id] === true && row && !this.hasChildren(id);
        });

        this.$emit("set-selected-rows", rowsArray);
      },
    },

    isCollapsed: {
      immediate: true,
      handler(value) {
        this.$emit("collapse-state-change", value);
      },
    },
  },
  mounted() {
    this.getSectorsTypes();

    window.addEventListener("resize", this.updateViewport);
  },
  created() {
    this.fetchProfileNotes(this.profileId);
    eventBus.$on("moveColumnByIndex", this.moveColumnByIndex);
    eventBus.$on("toggleColumnVisibility", this.toggleColumnVisibility);
  },
  beforeDestroy() {
    eventBus.$off("moveColumnByIndex", this.moveColumnByIndex);
    eventBus.$off("toggleColumnVisibility", this.toggleColumnVisibility);
    if (this.highlightTimer) {
      clearTimeout(this.highlightTimer);
    }
    if (this.animationCleanupTimer) {
      clearTimeout(this.animationCleanupTimer);
    }
    window.removeEventListener("resize", this.updateViewport);
  },
  provide() {
    return {
      tableContext: this,
      tooltipApi: this.tooltipController,
      notesApi: this.notesController,
      exceptionWordsApi: this.exceptionWordsController,
    };
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/less/variables.less";

.purchases-table-wrapper {
  display: flex;
  flex-direction: column;

  * {
    box-sizing: border-box;
  }
  .table-head {
    flex: 0 0 auto;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    padding-right: 8px;

    &::-webkit-scrollbar {
      display: none;
    }

    .table-head-content {
      width: max-content;
      will-change: transform;
    }
  }

  .table-body {
    flex: 1 1 auto;
    overflow-anchor: none;
    height: calc(100% - 74px);
    overflow: auto;
    position: relative;
    z-index: 1;
    &::-webkit-scrollbar {
      border-radius: @br;
      width: 8px;
      height: 8px;
      background-color: #ffffff;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: @br;
      width: 8px;
      background-color: fade(#000000, 24%);
      &:hover {
        background-color: fade(#000000, 30%);
      }
    }
    &::-webkit-scrollbar-track {
      border-radius: @br;
      background-color: rgb(248, 247, 247);
    }
    .table-content {
      will-change: transform;
      position: relative;
      background-color: white;
    }
  }
}
</style>
